import React, { useState, useRef, useEffect } from "react";
import { LogOut, ChevronDown, Menu, Shield, Bell } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { ThemeToggle } from "@/components/ui/ThemeToggle/ThemeToggle";

type TopbarProps = { onToggleSidebar: () => void };

export const Topbar: React.FC<TopbarProps> = ({ onToggleSidebar }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user, logout } = useAuth();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = () => {
    setShowUserMenu(false);
    logout();
  };

  const displayName = user?.email?.split("@")[0] || "Admin";
  const initial = displayName.charAt(0).toUpperCase();

  return (
    <header className="h-[4.25rem] px-4 sm:px-6 flex items-center justify-between gap-3 bg-lyrax-surface/80 backdrop-blur-md border-b border-lyrax-border/60 relative z-20 shrink-0">
      <button
        onClick={onToggleSidebar}
        className="p-2 rounded-lyrax text-lyrax-text-secondary hover:bg-lyrax-surface-hover hover:text-lyrax-text transition-colors"
        aria-label="Alternar menú"
      >
        <Menu className="w-5 h-5" />
      </button>

      <div className="flex items-center gap-2 sm:gap-3">
        <div className="hidden md:flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-lyrax-primary/10">
          <Shield className="w-3.5 h-3.5 text-lyrax-primary" />
          <span className="text-[11px] font-semibold text-lyrax-primary tracking-wide">
            Super Admin
          </span>
        </div>

        <ThemeToggle />

        <button
          type="button"
          className="hidden sm:flex p-2 rounded-full text-lyrax-text-secondary hover:bg-lyrax-surface-hover hover:text-lyrax-text transition-colors"
          aria-label="Notificaciones"
        >
          <Bell className="w-5 h-5" />
        </button>

        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-lyrax-surface-hover transition-colors"
          >
            <div className="w-8 h-8 rounded-full bg-lyrax-primary flex items-center justify-center text-lyrax-primary-ink font-bold text-sm ">
              {initial}
            </div>
            <span className="text-sm font-semibold text-lyrax-text hidden sm:block capitalize">
              {displayName.replace(/[._]/g, " ")}
            </span>
            <ChevronDown className="w-4 h-4 text-lyrax-text-secondary hidden sm:block" />
          </button>

          {showUserMenu && (
            <div className="absolute right-0 mt-2 w-56 bg-lyrax-surface border border-lyrax-border rounded-lyrax shadow-lyrax py-1 z-50 animate-fade-up">
              <div className="px-4 py-3 border-b border-lyrax-border">
                <p className="text-sm font-medium text-lyrax-text truncate">{user?.email}</p>
                <p className="text-xs text-lyrax-text-secondary">Super Admin</p>
              </div>

              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-500/10 transition-colors"
              >
                <LogOut className="w-4 h-4" />
                Cerrar sesión
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
